import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, inr, formatDate, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Bits";
import CameraCaptureButton from "@/components/CameraCaptureButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ComboSelect from "@/components/ComboSelect";
import DuplicateWarningBanner from "@/components/DuplicateWarningBanner";
import { toast, Toaster } from "sonner";
import {
    Wallet, Plus, CheckCircle, XCircle, PaperPlaneTilt, Paperclip,
    PencilSimple, Trash, ArrowsClockwise, Eye,
} from "@phosphor-icons/react";

const TABS_BY_ROLE = {
    founder: ["mine", "approvals", "disburse", "team"],
    accountant: ["mine", "disburse"],
    purchase: ["mine"],
    sales: ["mine"],
};

const TAB_LABELS = {
    mine: "My petty cash",
    approvals: "Approvals",
    disburse: "To disburse",
    team: "Team",
};

export default function PettyCash() {
    const { user } = useAuth();
    const tabs = TABS_BY_ROLE[user?.role] || ["mine"];
    const [searchParams] = useSearchParams();
    // Deep-link: /petty-cash?tab=disburse lands directly on To-disburse.
    const initialTab = (() => {
        const t = searchParams.get("tab");
        return t && tabs.includes(t) ? t : tabs[0];
    })();
    const [tab, setTab] = useState(initialTab);
    const [summary, setSummary] = useState(null);
    const [requests, setRequests] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [heads, setHeads] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [s, r, e, se, lg] = await Promise.all([
                api.get("/petty-cash/summary"),
                api.get("/petty-cash/requests"),
                api.get("/petty-cash/expenses"),
                api.get("/petty-cash/settings"),
                api.get("/ledgers"),
            ]);
            setSummary(s.data);
            setRequests(r.data);
            setExpenses(e.data);
            setHeads(se.data.heads || []);
            setLedgers(lg.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const bankCashLedgers = useMemo(
        () => ledgers.filter((l) => l.party_type === "bank" || l.party_type === "cash"),
        [ledgers],
    );

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Petty cash"
                sub={`Request advances, account expenses with proof, all in one place.${user?.role === "founder" ? " As founder you also approve requests and review the team." : ""}`}
                right={
                    <Button variant="outline" onClick={refresh} disabled={loading} data-testid="pc-refresh">
                        <ArrowsClockwise size={14} className="mr-2" /> Refresh
                    </Button>
                }
            />

            <div className="px-8 pt-4 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="flex gap-1" data-testid="pc-tabs">
                    {tabs.map((t) => (
                        <button key={t} onClick={() => setTab(t)}
                            className={`px-4 py-2 text-sm rounded-t-sm border-b-2 ${tab === t ? "font-medium" : "text-stone-500"}`}
                            style={{ borderColor: tab === t ? "#f59e0b" : "transparent", color: tab === t ? "#0f172a" : undefined }}
                            data-testid={`pc-tab-${t}`}
                        >{TAB_LABELS[t]}</button>
                    ))}
                </div>
            </div>

            <div className="p-8 space-y-6">
                {tab === "mine" && (
                    <MinePane summary={summary?.own} heads={heads} ledgers={ledgers} requests={requests}
                              expenses={expenses} onChanged={refresh} />
                )}
                {tab === "approvals" && (
                    <ApprovalsPane requests={requests} onChanged={refresh} />
                )}
                {tab === "disburse" && (
                    <DisbursePane requests={requests} bankCashLedgers={bankCashLedgers} onChanged={refresh} />
                )}
                {tab === "team" && (
                    <TeamPane team={summary?.team || []} cap={summary?.cap} />
                )}
            </div>
        </>
    );
}

// ============================================================================
// My Petty Cash — everyone sees this
// ============================================================================
function MinePane({ summary, heads, ledgers, requests, expenses, onChanged }) {
    const [reqOpen, setReqOpen] = useState(false);
    const [expOpen, setExpOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    if (!summary) return <div className="text-stone-500">Loading…</div>;
    const inHand = summary.in_hand || 0;
    const cap = summary.cap || 0;
    const canRequest = summary.can_request;
    const myRequests = requests.filter((r) => r.employee_id === summary.user_id);
    const myExpenses = expenses.filter((e) => e.employee_id === summary.user_id);

    return (
        <>
            <div className="grid grid-cols-3 gap-4">
                <StatCard
                    label="In hand right now"
                    value={inHand}
                    tone={inHand >= cap ? "amber" : "teal"}
                    hint={inHand >= cap ? "You've hit the unaccounted limit — upload proofs to unlock a new request" : "Money you're holding on behalf of the company"}
                    bold
                    testid="pc-in-hand"
                />
                <StatCard label="Unaccounted limit" value={cap} tone="dark" hint="Hold less than this to raise a new request" testid="pc-cap" />
                <StatCard label="Total accounted" value={myExpenses.filter((e) => e.status === "posted").reduce((s, e) => s + e.amount, 0)} tone="dark" hint={`${myExpenses.filter((e) => e.status === "posted").length} expense${myExpenses.length === 1 ? "" : "s"}`} testid="pc-total-accounted" />
            </div>

            <div className="flex flex-wrap gap-3">
                <Button onClick={() => setReqOpen(true)} disabled={!canRequest}
                        style={{ background: canRequest ? "#f59e0b" : "#a1a1aa", color: "#fff" }}
                        data-testid="pc-new-request-btn">
                    <PaperPlaneTilt size={14} className="mr-2" /> New advance request
                </Button>
                <Button variant="outline" onClick={() => { setEditingExpense(null); setExpOpen(true); }}
                        disabled={inHand <= 0.01 || heads.length === 0}
                        data-testid="pc-new-expense-btn">
                    <Plus size={14} className="mr-2" /> Account an expense
                </Button>
                {heads.length === 0 && <div className="text-xs text-stone-500 self-center">(Founder needs to add expense heads in Settings first)</div>}
            </div>

            <PettySection title="My requests" testid="pc-my-requests">
                <table className="se-table">
                    <thead><tr><th>Date</th><th>Amount</th><th>Reason</th><th>Status</th><th>Detail</th></tr></thead>
                    <tbody>
                        {myRequests.length === 0
                            ? <tr><td colSpan="5" className="py-6 text-center text-stone-500">No requests yet.</td></tr>
                            : myRequests.map((r) => <RequestRow key={r.id} r={r} showOwner={false} onChanged={onChanged} />)}
                    </tbody>
                </table>
            </PettySection>

            <PettySection title="My accounted expenses" testid="pc-my-expenses">
                <table className="se-table">
                    <thead><tr><th>Date</th><th>Head</th><th>Amount</th><th>Note</th><th>Proof</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                        {myExpenses.length === 0
                            ? <tr><td colSpan="7" className="py-6 text-center text-stone-500">No expenses accounted yet.</td></tr>
                            : myExpenses.map((e) => (
                                <ExpenseRow key={e.id} e={e}
                                    onEdit={() => { setEditingExpense(e); setExpOpen(true); }}
                                    onChanged={onChanged} />
                            ))}
                    </tbody>
                </table>
            </PettySection>

            <RequestDialog open={reqOpen} onOpenChange={setReqOpen} onDone={onChanged} inHand={inHand} cap={cap} />
            <SubmitBillDialog
                open={expOpen} onOpenChange={(o) => { setExpOpen(o); if (!o) setEditingExpense(null); }}
                onDone={onChanged} editing={editingExpense}
            />
        </>
    );
}


// ============================================================================
// SubmitBillDialog — Staff form (Wave 1)
// ============================================================================
// Deliberately minimal — the goal is to let staff snap a bill and file it
// without needing any accounting knowledge. AI extraction, expense-head
// mapping, GST classification, and vendor routing ALL move to the
// approver's popup (ApproveExpenseDialog below). Rejected submissions
// come back here for a "Fix & resubmit" cycle.
// ============================================================================
function SubmitBillDialog({ open, onOpenChange, onDone, editing }) {
    const isResubmit = editing && editing.status === "rejected";
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [note, setNote] = useState("");
    const [file, setFile] = useState(null);
    const [existingDocId, setExistingDocId] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            if (editing) {
                setAmount(String(editing.amount || ""));
                setDate(editing.date || new Date().toISOString().slice(0, 10));
                setNote(editing.note || "");
                setFile(null);
                setExistingDocId(editing.document_id || null);
            } else {
                setAmount(""); setDate(new Date().toISOString().slice(0, 10));
                setNote(""); setFile(null); setExistingDocId(null);
            }
        }
    }, [open, editing]);

    const submit = async () => {
        const a = Number(amount);
        if (!a || a <= 0) return toast.error("Amount must be positive");
        if (!file && !existingDocId) return toast.error("Attach the bill — proof is mandatory.");
        setSaving(true);
        try {
            let docId = existingDocId;
            if (file) {
                const fd = new FormData(); fd.append("file", file);
                const { data } = await api.post("/petty-cash/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
                docId = data.document_id;
            }
            const body = { amount: a, date, note: note || null, document_id: docId };
            if (isResubmit) {
                await api.post(`/petty-cash/expenses/${editing.id}/resubmit`, body);
                toast.success("Resubmitted for approval");
            } else {
                await api.post("/petty-cash/expenses", body);
                toast.success("Submitted — the accountant will review your bill.");
            }
            onOpenChange(false);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="pc-submit-dialog">
                <DialogHeader>
                    <DialogTitle>{isResubmit ? "Fix & resubmit bill" : "Submit a bill"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {isResubmit && editing?.rejection_reason ? (
                        <div className="border rounded-sm p-3 text-[11px]"
                             style={{ borderColor: "#e11d48", background: "#fef2f2", color: "#991b1b" }}>
                            <b>Rejected — reason:</b> {editing.rejection_reason}
                        </div>
                    ) : null}
                    <div className="text-[11px] text-stone-500">
                        The accountant reviews every bill and does the accounting for you. Just attach the bill + amount and submit.
                    </div>

                    <div className="border rounded-sm p-3" style={{ borderColor: "var(--se-border)" }}>
                        <Label>Bill (image or PDF) *</Label>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-sm text-xs cursor-pointer hover:bg-stone-50"
                                   style={{ borderColor: "var(--se-border)" }} data-testid="pc-submit-file">
                                <Paperclip size={13} /> {file ? file.name : (existingDocId ? "Replace attachment" : "Attach bill")}
                                <input type="file" accept="image/*,application/pdf" className="hidden"
                                       onChange={(e) => setFile(e.target.files?.[0] || null)} />
                            </label>
                            <CameraCaptureButton onFile={setFile} label="Camera" testid="pc-submit-camera" />
                            {existingDocId && !file
                                ? <a href={`${API}/documents/${existingDocId}/download`} target="_blank" rel="noopener noreferrer"
                                     className="text-[11px] underline text-blue-700">view current</a>
                                : null}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Amount paid (₹) *</Label>
                            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pc-submit-amount" /></div>
                        <div><Label>Date *</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="pc-submit-date" /></div>
                    </div>
                    <div><Label>Note</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)}
                               placeholder="e.g. Petrol pump receipt / office chai / hardware for jig" data-testid="pc-submit-note" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving}
                            style={{ background: "#f59e0b", color: "#fff" }}
                            data-testid="pc-submit-btn">
                        {saving ? "Submitting…" : (isResubmit ? "Resubmit" : "Submit for approval")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// Approvals (founder only)
// ============================================================================
function ApprovalsPane({ requests, onChanged }) {
    const pending = requests.filter((r) => r.status === "pending");
    const acted = requests.filter((r) => ["approved", "rejected", "cancelled", "disbursed"].includes(r.status));
    const [pendingBills, setPendingBills] = useState([]);
    const [reviewing, setReviewing] = useState(null);
    const [heads, setHeads] = useState([]);
    const [ledgers, setLedgers] = useState([]);

    const refreshBills = useCallback(async () => {
        try {
            const { data } = await api.get("/petty-cash/expenses/pending-approval");
            setPendingBills(data || []);
        } catch (_e) { /* non-critical */ }
    }, []);
    useEffect(() => {
        refreshBills();
        (async () => {
            try {
                const [s, l] = await Promise.all([
                    api.get("/petty-cash/settings"),
                    api.get("/ledgers"),
                ]);
                setHeads((s.data.heads || []).filter((h) => h.active));
                setLedgers(l.data || []);
            } catch (_e) { /* non-critical */ }
        })();
    }, [refreshBills]);

    return (
        <>
            <PettySection title={`Bill submissions awaiting review — ${pendingBills.length}`} accent="#f59e0b" testid="pc-bill-review-queue">
                <table className="se-table">
                    <thead><tr><th>Filed on</th><th>By</th><th>Amount</th><th>Note</th><th>Bill</th><th></th></tr></thead>
                    <tbody>
                        {pendingBills.length === 0
                            ? <tr><td colSpan="6" className="py-6 text-center text-stone-500">No bills to classify. 🎉</td></tr>
                            : pendingBills.map((b) => (
                                <tr key={b.id} data-testid={`pc-bill-pending-${b.id}`}>
                                    <td className="font-mono text-xs">{formatDate(b.created_at)}</td>
                                    <td>{b.employee_name}</td>
                                    <td className="se-num font-medium">₹ {inr(b.amount)}</td>
                                    <td className="text-xs">{b.note || "—"}</td>
                                    <td>
                                        {b.document_id
                                            ? <a href={`${API}/documents/${b.document_id}/download`} target="_blank" rel="noopener noreferrer"
                                                 className="text-[11px] underline text-blue-700">view</a>
                                            : <span className="text-[11px] text-stone-400">—</span>}
                                    </td>
                                    <td>
                                        <div className="flex justify-end">
                                            <Button size="sm" onClick={() => setReviewing(b)}
                                                    style={{ background: "#f59e0b", color: "#fff" }}
                                                    data-testid={`pc-review-${b.id}`}>
                                                Review & classify →
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </PettySection>

            <PettySection title={`Pending — ${pending.length}`} accent="#f59e0b" testid="pc-pending-queue">
                <table className="se-table">
                    <thead><tr><th>Raised on</th><th>By</th><th>Amount</th><th>Reason</th><th>Their in-hand</th><th></th></tr></thead>
                    <tbody>
                        {pending.length === 0
                            ? <tr><td colSpan="6" className="py-6 text-center text-stone-500">Nothing to approve. 🎉</td></tr>
                            : pending.map((r) => (
                                <tr key={r.id} data-testid={`pc-pending-${r.id}`}>
                                    <td className="font-mono text-xs">{formatDate(r.created_at)}</td>
                                    <td>{r.employee_name} <span className="text-[10px] text-stone-500">({r.employee_role})</span></td>
                                    <td className="se-num font-medium">₹ {inr(r.amount)}</td>
                                    <td className="text-xs">{r.reason}</td>
                                    <td className="se-num text-xs text-stone-500">₹ {inr(r.in_hand_at_request)}</td>
                                    <td>
                                        <div className="flex gap-2 justify-end">
                                            <Button size="sm" variant="outline" onClick={async () => {
                                                const reason = window.prompt("Reject reason?");
                                                if (!reason) return;
                                                try { await api.post(`/petty-cash/requests/${r.id}/reject`, { reason }); toast.success("Rejected"); onChanged(); }
                                                catch (e) { toast.error(formatApiError(e)); }
                                            }} data-testid={`pc-reject-${r.id}`}>
                                                <XCircle size={14} className="mr-1" /> Reject
                                            </Button>
                                            <Button size="sm" onClick={async () => {
                                                try { await api.post(`/petty-cash/requests/${r.id}/approve`); toast.success("Approved"); onChanged(); }
                                                catch (e) { toast.error(formatApiError(e)); }
                                            }} style={{ background: "#16a34a", color: "#fff" }} data-testid={`pc-approve-${r.id}`}>
                                                <CheckCircle size={14} className="mr-1" /> Approve
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </PettySection>

            <PettySection title={`Recently acted — ${acted.length}`} testid="pc-acted">
                <table className="se-table">
                    <thead><tr><th>Date</th><th>By</th><th>Amount</th><th>Reason</th><th>Status</th></tr></thead>
                    <tbody>
                        {acted.length === 0
                            ? <tr><td colSpan="5" className="py-6 text-center text-stone-500">No history yet.</td></tr>
                            : acted.slice(0, 50).map((r) => <RequestRow key={r.id} r={r} showOwner onChanged={onChanged} />)}
                    </tbody>
                </table>
            </PettySection>

            {/* Review popup — reuses ExpenseDialog with the submission pre-loaded. */}
            <ExpenseDialog
                open={!!reviewing}
                onOpenChange={(o) => { if (!o) setReviewing(null); }}
                onDone={() => { setReviewing(null); refreshBills(); onChanged(); }}
                heads={heads} ledgers={ledgers} inHand={0}
                editing={reviewing} />
        </>
    );
}

// ============================================================================
// Disburse queue — founder + accountant
// ============================================================================
function DisbursePane({ requests, bankCashLedgers, onChanged }) {
    const [disbursing, setDisbursing] = useState(null); // request being disbursed
    const [source, setSource] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [saving, setSaving] = useState(false);
    const queue = requests.filter((r) => r.status === "approved");
    useEffect(() => {
        if (bankCashLedgers.length && !source) {
            const cash = bankCashLedgers.find((l) => l.name === "FIAT BANK") || bankCashLedgers[0];
            setSource(cash.id);
        }
    }, [bankCashLedgers, source]);

    const submit = async () => {
        if (!source) return toast.error("Pick a source");
        setSaving(true);
        try {
            const { data } = await api.post(`/petty-cash/requests/${disbursing.id}/disburse`, { source_ledger_id: source, date });
            if (data.mode === "pending_bank_match") {
                toast.success(`Queued ₹${inr(disbursing.amount)} against ${data.bank_ledger_name} — will post when the bank statement is uploaded`);
            } else {
                toast.success(`Disbursed ₹${inr(disbursing.amount)} to ${disbursing.employee_name}`);
            }
            setDisbursing(null);
            onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    // Show a hint under the source picker so accountants know what will happen.
    const chosenSource = bankCashLedgers.find((l) => l.id === source);
    const isBankSource = chosenSource && chosenSource.party_type === "bank" && !chosenSource.system;

    return (
        <>
            <PettySection title={`Approved — waiting for disbursement · ${queue.length}`} accent="#0d6efd" testid="pc-disburse-queue">
                <table className="se-table">
                    <thead><tr><th>Approved on</th><th>Employee</th><th>Amount</th><th>Reason</th><th></th></tr></thead>
                    <tbody>
                        {queue.length === 0
                            ? <tr><td colSpan="5" className="py-6 text-center text-stone-500">Nothing waiting to be disbursed.</td></tr>
                            : queue.map((r) => (
                                <tr key={r.id} data-testid={`pc-disburse-row-${r.id}`}>
                                    <td className="font-mono text-xs">{formatDate(r.approved_at)}</td>
                                    <td>{r.employee_name}</td>
                                    <td className="se-num font-medium">₹ {inr(r.amount)}</td>
                                    <td className="text-xs">{r.reason}</td>
                                    <td>
                                        <Button size="sm" onClick={() => setDisbursing(r)}
                                                style={{ background: "#0d6efd", color: "#fff" }}
                                                data-testid={`pc-disburse-btn-${r.id}`}>
                                            <Wallet size={14} className="mr-1" /> Disburse
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </PettySection>

            <Dialog open={!!disbursing} onOpenChange={(o) => { if (!o) setDisbursing(null); }}>
                <DialogContent data-testid="pc-disburse-dialog">
                    <DialogHeader><DialogTitle>Disburse petty cash</DialogTitle></DialogHeader>
                    {disbursing && (
                        <div className="space-y-4">
                            <div className="text-sm text-stone-600">
                                Paying <b>₹{inr(disbursing.amount)}</b> to <b>{disbursing.employee_name}</b><br />
                                <span className="text-xs text-stone-500">for: {disbursing.reason}</span>
                            </div>
                            <div>
                                <Label>Paid from</Label>
                                <select className="w-full border rounded-sm px-3 py-2 text-sm" value={source}
                                        onChange={(e) => setSource(e.target.value)} data-testid="pc-disburse-source">
                                    {bankCashLedgers.map((l) => (
                                        <option key={l.id} value={l.id}>{l.name}</option>
                                    ))}
                                </select>
                                {isBankSource ? (
                                    <p className="text-[11px] text-stone-500 mt-1" data-testid="pc-disburse-bank-hint">
                                        Money will be booked only when this debit lands in the <b>{chosenSource.name}</b> statement — this prevents double entries.
                                    </p>
                                ) : (
                                    <p className="text-[11px] text-stone-500 mt-1">Cash / Fiat ledgers post the journal immediately.</p>
                                )}
                            </div>
                            <div>
                                <Label>Payment date</Label>
                                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="pc-disburse-date" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDisbursing(null)}>Cancel</Button>
                        <Button onClick={submit} disabled={saving} style={{ background: "#0d6efd", color: "#fff" }} data-testid="pc-disburse-confirm">
                            {saving ? "Posting…" : "Post disbursement"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

// ============================================================================
// Team roll-up (founder only)
// ============================================================================
function TeamPane({ team, cap }) {
    const total = team.reduce((s, t) => s + t.in_hand, 0);
    return (
        <>
            <div className="grid grid-cols-3 gap-4">
                <StatCard label="People holding petty cash" value={team.filter((t) => t.in_hand > 0.01).length} isCount tone="dark" testid="pc-team-count" />
                <StatCard label="Grand total in hand" value={total} tone="amber" bold testid="pc-team-total" />
                <StatCard label="Current limit" value={cap || 0} tone="dark" testid="pc-team-cap" />
            </div>
            <PettySection title="Team in-hand" testid="pc-team-table">
                <table className="se-table">
                    <thead><tr><th>Name</th><th>Role</th><th>In hand</th><th>Cap</th><th>Status</th></tr></thead>
                    <tbody>
                        {team.length === 0
                            ? <tr><td colSpan="5" className="py-6 text-center text-stone-500">No team members have petty cash yet.</td></tr>
                            : team.map((t) => (
                                <tr key={t.user_id} data-testid={`pc-team-row-${t.user_id}`}>
                                    <td>{t.user_name}</td>
                                    <td className="text-xs text-stone-500">{t.user_role}</td>
                                    <td className="se-num font-medium">₹ {inr(t.in_hand)}</td>
                                    <td className="se-num text-xs text-stone-500">₹ {inr(t.cap)}</td>
                                    <td>
                                        {t.in_hand >= t.cap
                                            ? <span className="se-chip se-chip-warn text-[10px]">blocked</span>
                                            : t.in_hand > 0
                                                ? <span className="se-chip se-chip-teal text-[10px]">holding</span>
                                                : <span className="se-chip se-chip-draft text-[10px]">clear</span>}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </PettySection>
        </>
    );
}

// ============================================================================
// Reusable rows + dialogs
// ============================================================================
function RequestRow({ r, showOwner, onChanged }) {
    // Awaiting-bank is an internal reconciliation state — from the
    // employee's perspective the money is already in their hand, so we
    // paint it as "disbursed" in the chip and the detail text.
    const effectiveStatus = r.status === "pending_bank_disbursement" ? "disbursed" : r.status;
    const chipCls = effectiveStatus === "pending" ? "se-chip-draft"
        : effectiveStatus === "approved" ? "se-chip-teal"
        : effectiveStatus === "disbursed" ? "se-chip-posted"
        : effectiveStatus === "rejected" ? "se-chip-err"
        : "se-chip-draft";
    const statusLabel = effectiveStatus;
    const sourceName = r.source_ledger_name || r.intended_source_ledger_name || "—";
    const disburseDate = r.disbursement_date || r.queued_at || r.updated_at;
    return (
        <tr data-testid={`pc-request-${r.id}`}>
            <td className="font-mono text-xs">{formatDate(r.created_at)}</td>
            {showOwner ? <td>{r.employee_name}</td> : null}
            <td className="se-num font-medium">₹ {inr(r.amount)}</td>
            <td className="text-xs">{r.reason}</td>
            <td><span className={`se-chip ${chipCls} text-[10px]`}>{statusLabel}</span></td>
            <td className="text-[11px] text-stone-500">
                {r.status === "rejected" && r.rejection_reason ? <>Reason: {r.rejection_reason}</>
                    : effectiveStatus === "disbursed" ? <>From {sourceName} on {formatDate(disburseDate)}</>
                    : r.status === "pending" ? (
                        <button className="underline hover:text-rose-600" onClick={async () => {
                            if (!window.confirm("Cancel this request?")) return;
                            try { await api.post(`/petty-cash/requests/${r.id}/cancel`); toast.success("Cancelled"); onChanged(); }
                            catch (e) { toast.error(formatApiError(e)); }
                        }} data-testid={`pc-cancel-${r.id}`}>Cancel</button>
                    ) : null}
            </td>
        </tr>
    );
}

function ExpenseRow({ e, onEdit, onChanged }) {
    const del = async () => {
        if (!window.confirm(`Delete this ₹${inr(e.amount)} expense? The ledger entry will be reversed.`)) return;
        try { await api.delete(`/petty-cash/expenses/${e.id}`); toast.success("Deleted"); onChanged(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    const statusChip = (() => {
        if (e.status === "posted") return <span className="se-chip se-chip-posted text-[10px]">posted</span>;
        if (e.status === "pending_approval") return <span className="se-chip se-chip-draft text-[10px]">awaiting review</span>;
        if (e.status === "rejected") return <span className="se-chip se-chip-err text-[10px]">rejected</span>;
        return <span className="se-chip se-chip-err text-[10px]">reversed</span>;
    })();
    return (
        <tr data-testid={`pc-expense-${e.id}`} style={e.status === "reversed" ? { opacity: 0.5 } : undefined}>
            <td className="font-mono text-xs">{e.date}</td>
            <td>{e.head_name || <span className="text-[10px] text-stone-400">— awaiting classification —</span>}
                {e.has_gst ? <div className="text-[10px] text-stone-500 mt-0.5">
                    GST · {e.supplier_name || "—"} · #{e.invoice_no || "—"}
                </div> : null}
                {e.status === "rejected" && e.rejection_reason ? (
                    <div className="text-[10px] text-rose-700 mt-0.5" title={e.rejection_reason}>
                        Reason: {e.rejection_reason.length > 60 ? e.rejection_reason.slice(0, 60) + "…" : e.rejection_reason}
                    </div>
                ) : null}
            </td>
            <td className="se-num font-medium">₹ {inr(e.amount)}
                {e.has_gst && e.gst ? <div className="text-[10px] text-stone-500">
                    ITC ₹ {inr(e.gst.gst_amount || 0)} ({e.gst.is_igst ? "IGST" : "CGST+SGST"})
                </div> : null}
            </td>
            <td className="text-xs text-stone-500">{e.note || "—"}</td>
            <td>
                {e.document_id
                    ? <a href={`${API}/documents/${e.document_id}/download`} target="_blank" rel="noopener noreferrer"
                         className="text-[11px] underline hover:text-blue-700 inline-flex items-center gap-1"
                         data-testid={`pc-proof-link-${e.id}`}>
                        <Eye size={12} /> view
                      </a>
                    : <span className="text-[11px] text-stone-400">—</span>}
            </td>
            <td>{statusChip}</td>
            <td>
                {e.status === "rejected" ? (
                    <Button size="sm" variant="outline" onClick={onEdit}
                            data-testid={`pc-resubmit-${e.id}`}>Fix & resubmit</Button>
                ) : e.status === "posted" ? (
                    <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={del} data-testid={`pc-delete-${e.id}`}><Trash size={13} className="text-rose-600" /></Button>
                    </div>
                ) : null}
            </td>
        </tr>
    );
}

function RequestDialog({ open, onOpenChange, onDone, inHand, cap }) {
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) { setAmount(""); setReason(""); } }, [open]);
    const submit = async () => {
        const a = Number(amount);
        if (!a || a <= 0) return toast.error("Amount must be positive");
        if (!reason.trim()) return toast.error("Reason is required");
        setSaving(true);
        try {
            await api.post("/petty-cash/requests", { amount: a, reason });
            toast.success("Request submitted — awaiting approval");
            onOpenChange(false);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="pc-request-dialog">
                <DialogHeader><DialogTitle>New petty cash request</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="p-3 bg-amber-50 border rounded-sm text-[11px]" style={{ borderColor: "var(--se-border)" }}>
                        You are currently holding <b>₹ {inr(inHand)}</b> unaccounted. The limit is <b>₹ {inr(cap)}</b> — you can only raise a new request while you&apos;re holding <b>less than</b> that.<br />
                        <span className="text-stone-500">Request amount is uncapped — ask for whatever you need. But every rupee you take is added to your kitty; upload proofs to bring it back down.</span>
                    </div>
                    <div><Label>Amount (₹) *</Label>
                        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pc-request-amount" /></div>
                    <div><Label>Reason *</Label>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)}
                               placeholder="e.g. Fuel for factory pickup Monday" data-testid="pc-request-reason" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="pc-request-submit">
                        {saving ? "Sending…" : "Send for approval"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function VendorRoutingPanel({ isGstBill, supplierName, supplierGstin,
                              ledgers, pickedVendorId, setPickedVendorId,
                              skipVendorRouting, setSkipVendorRouting }) {
    // Existing vendor ledgers under Sundry Creditors — options for Tier 2.
    const vendorOptions = useMemo(() =>
        (ledgers || [])
            .filter((l) => l.party_type === "vendor" && l.active !== false)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((l) => ({ id: l.id, name: l.name, hint: l.group_name })),
    [ledgers]);

    // Route A — GST bill with matched buyer GSTIN. Vendor is auto-created
    // from the supplier name/GSTIN. Panel is greyed out — no action needed.
    if (isGstBill) {
        return (
            <div className="border rounded-sm p-3 flex items-start gap-3 opacity-70 pointer-events-none"
                 style={{ borderColor: "var(--se-border)", background: "#f5f5f4" }}
                 data-testid="pc-vendor-auto-disabled">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ background: "#0f766e", color: "#fff", fontSize: 14 }}>✓</div>
                <div className="flex-1">
                    <div className="text-sm font-medium text-stone-700">Vendor ledger auto-routed</div>
                    <div className="text-[11px] text-stone-600 mt-0.5">
                        This is a valid GST bill — <b>{supplierName || "the supplier"}</b>
                        {supplierGstin ? ` (${supplierGstin})` : ""} will be booked into their
                        vendor ledger automatically. No action needed here.
                    </div>
                </div>
            </div>
        );
    }

    // Route B / C — AI didn't confirm a GST bill. Approver picks a vendor
    // OR ticks "Skip vendor mapping for this invoice" for a direct expense.
    return (
        <div className="border rounded-sm p-3"
             style={{ borderColor: "#f59e0b", background: "#fffbeb" }}
             data-testid="pc-vendor-picker">
            <div className="text-sm font-medium text-amber-900 mb-1">Map to a vendor?</div>
            <div className="text-[11px] text-amber-800 mb-2">
                Pick an existing vendor to route this expense through their ledger. If it&apos;s a
                small cash memo with no vendor to track, tick <b>Skip vendor mapping</b> — the
                amount will land straight in the expense head.
            </div>
            <div className="flex items-start gap-2">
                <div className="flex-1">
                    <ComboSelect
                        value={pickedVendorId}
                        onValueChange={setPickedVendorId}
                        options={vendorOptions}
                        placeholder={vendorOptions.length ? "Search vendors…" : "No vendors yet"}
                        size="sm"
                        disabled={skipVendorRouting}
                        testId="pc-vendor-select"
                    />
                </div>
            </div>
            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="pc-vendor-skip-wrap">
                <input type="checkbox" checked={skipVendorRouting}
                       onChange={(e) => {
                           setSkipVendorRouting(e.target.checked);
                           if (e.target.checked) setPickedVendorId("");
                       }}
                       className="cursor-pointer" data-testid="pc-vendor-skip" />
                <span className="text-[11px] text-amber-900">
                    Skip vendor mapping for this invoice (book as a direct expense)
                </span>
            </label>
        </div>
    );
}


// Neutral "AI couldn't confirm this is a GST bill" banner — used in place
// of the ITC-auto-claimed banner when supplier GSTIN or our GSTIN is
// missing from the bill. Signals to the approver: no ITC will be
// claimed; pick the expense head and (optionally) a vendor manually.

// GST Sec 17(5) blocked-credit banner — shown in place of the green
// ITC banner when the picked head is on the block-list (F&B, motor
// vehicles, employee welfare, etc.). Tells the approver that the GST
// portion will be booked as gross expense (no Input GST split).
function BlockedCreditBanner({ head }) {
    return (
        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="pc-blocked-credit-banner"
             style={{ background: "#fef2f2", borderColor: "#fca5a5", color: "#991b1b" }}>
            <div className="font-medium flex items-center gap-2">
                <span aria-hidden="true">🚫</span> Blocked credit — Sec {head?.blocked_credit_section || "17(5)"}
            </div>
            <div className="text-[11px] mt-1 opacity-90">
                <b>{head?.blocked_credit_reason || "Ineligible ITC"}</b> — the GST amount will be
                booked gross into the expense head. No Input GST will be claimed on this bill.
            </div>
        </div>
    );
}

function FailedGstDetectBanner() {
    return (
        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="pc-gst-detect-failed"
             style={{ background: "#f8fafc", borderColor: "#cbd5e1", color: "#334155" }}>
            <div className="font-medium flex items-center gap-2">
                <span aria-hidden="true">ℹ</span> Failed to detect GST bill
            </div>
            <div className="text-[11px] mt-1 opacity-90">
                The AI could not confirm both the supplier&apos;s GSTIN and your company&apos;s
                GSTIN on this bill — treating it as a non-GST expense. Pick an expense
                head (mandatory) and optionally a vendor below.
            </div>
        </div>
    );
}

// Amber "possible duplicate" banner — appears on the approver popup when the
// same supplier GSTIN + invoice_no already exists in another module.
// Layer 2 of duplicate-bill detection (soft warn, non-blocking). See
// components/DuplicateWarningBanner.jsx for the shared implementation.




function ExpenseDialog({ open, onOpenChange, onDone, heads, ledgers, inHand, editing }) {
    const [headId, setHeadId] = useState("");
    const [ledgerOverrideId, setLedgerOverrideId] = useState("");
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [file, setFile] = useState(null);
    const [existingDocId, setExistingDocId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [homeStateCode, setHomeStateCode] = useState("27");
    // GST-invoice fields
    const [hasGst, setHasGst] = useState(false);
    const [supplierName, setSupplierName] = useState("");
    const [supplierGstin, setSupplierGstin] = useState("");
    const [invoiceNo, setInvoiceNo] = useState("");
    const [invoiceDate, setInvoiceDate] = useState("");
    const [taxable, setTaxable] = useState("");
    const [gstRate, setGstRate] = useState("18");
    const [extracting, setExtracting] = useState(false);
    // Buyer sanity — populated by AI extract; server re-verifies on submit.
    const [buyerNameOnBill, setBuyerNameOnBill] = useState("");
    const [buyerGstinOnBill, setBuyerGstinOnBill] = useState("");
    const [buyerMatchStatus, setBuyerMatchStatus] = useState(""); // '' | 'match_gstin' | 'match_name_loose' | 'mismatch' | 'no_buyer_info' | 'no_gst' | 'no_company_setup'
    const [buyerMatchReason, setBuyerMatchReason] = useState("");
    const [looseConfirmed, setLooseConfirmed] = useState(false);
    // ITC-confirm gate (Feb 2026) — approver must explicitly tick this before
    // ITC is claimed. Independent from `hasGst` (which reflects OCR verdict).
    const [itcConfirmed, setItcConfirmed] = useState(false);
    // Snapshot of the OCR-extracted values, sent to backend for audit.
    const [aiExtracted, setAiExtracted] = useState(null);
    // Vendor-routing tier state — tells the backend how to split the journal.
    //   pickedVendorId + "" → server decides (Tier 1 auto for GST bills, else Tier 3).
    //   pickedVendorId set  → Tier 2 (user's explicit pick).
    //   skipVendorRouting=true → Tier 3 (direct-hit even if GST).
    const [pickedVendorId, setPickedVendorId] = useState("");
    const [skipVendorRouting, setSkipVendorRouting] = useState(false);
    const [showLooseConfirm, setShowLooseConfirm] = useState(false);
    const [duplicateWarnings, setDuplicateWarnings] = useState([]);

    // Pull real home state code once dialog opens so preview matches server behavior.
    useEffect(() => {
        if (open) {
            api.get("/petty-cash/settings/home-state")
                .then(({ data }) => setHomeStateCode(String(data.value || "27")))
                .catch(() => {});
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            if (editing) {
                setHeadId(editing.head_id);
                setLedgerOverrideId(editing.ledger_override_id || "");
                setAmount(String(editing.amount));
                setNote(editing.note || "");
                setDate(editing.date);
                setFile(null);
                setExistingDocId(editing.document_id || null);
                setHasGst(!!editing.has_gst);
                setSupplierName(editing.supplier_name || "");
                setSupplierGstin(editing.supplier_gstin || "");
                setInvoiceNo(editing.invoice_no || "");
                setInvoiceDate(editing.invoice_date || "");
                setTaxable(editing.gst?.taxable_value ? String(editing.gst.taxable_value) : "");
                setGstRate(editing.gst?.gst_rate != null ? String(editing.gst.gst_rate) : "18");
                setBuyerNameOnBill(editing.buyer_name_on_bill || "");
                setBuyerGstinOnBill(editing.buyer_gstin_on_bill || "");
                setBuyerMatchStatus(editing.buyer_match_status || "");
                setBuyerMatchReason("");
                setLooseConfirmed(true); // trust prior save
                // Approver review of a dumb-submitted bill — auto-run AI so
                // the popup opens *with* supplier/GST already filled in.
                // Skip if we already have supplier data (edit case).
                if (editing.status === "pending_approval"
                    && editing.document_id
                    && !editing.supplier_name
                    && !editing.has_gst) {
                    runRescan(editing.id);
                }
            } else {
                setHeadId(heads[0]?.id || "");
                setLedgerOverrideId("");
                setAmount(""); setNote(""); setDate(new Date().toISOString().slice(0, 10));
                setFile(null); setExistingDocId(null);
                setHasGst(false); setSupplierName(""); setSupplierGstin("");
                setInvoiceNo(""); setInvoiceDate(""); setTaxable(""); setGstRate("18");
                setBuyerNameOnBill(""); setBuyerGstinOnBill("");
                setBuyerMatchStatus(""); setBuyerMatchReason(""); setLooseConfirmed(false);
                setDuplicateWarnings([]);
                setItcConfirmed(false); setAiExtracted(null);
            }
        }
    }, [open, editing, heads]);

    // When GST toggle is on,

    // When GST toggle is on, auto-compute gross amount = taxable * (1 + rate/100)
    useEffect(() => {
        if (!hasGst) return;
        const t = Number(taxable); const r = Number(gstRate);
        if (!Number.isNaN(t) && !Number.isNaN(r) && t > 0) {
            const gross = +(t * (1 + r / 100)).toFixed(2);
            setAmount(String(gross));
        }
    }, [hasGst, taxable, gstRate]);

    const runRescan = async (eid) => {
        // Rescan the already-uploaded bill of a pending_approval submission.
        // Used when the approver opens Review & Classify — we run AI so
        // supplier / GST / expense head are pre-filled instead of blank.
        if (!eid) return;
        setExtracting(true);
        try {
            const { data } = await api.post(`/petty-cash/expenses/${eid}/rescan`);
            _applyExtract(data, /*fromRescan=*/true);
        } catch (e) {
            toast.error(`Auto-scan failed: ${formatApiError(e)}. Fill the details manually.`);
        } finally { setExtracting(false); }
    };

    // Shared setter used by both runExtract (fresh upload) and runRescan
    // (approver review). Kept as a local closure so it sees all the
    // setState fns without prop-drilling.
    const _applyExtract = (data, fromRescan) => {
        if (data.error) {
            toast.error(`AI couldn't read it: ${data.error}`);
            return;
        }
        if (!fromRescan) {
            setExistingDocId(data.document_id);
            setFile(null);
        }
        if (data.supplier_name) setSupplierName(data.supplier_name);
        if (data.supplier_gstin) setSupplierGstin(data.supplier_gstin);
        if (data.invoice_no) setInvoiceNo(data.invoice_no);
        if (data.invoice_date) setInvoiceDate(data.invoice_date);
        if (data.taxable_value) setTaxable(String(data.taxable_value));
        if (data.gst_rate != null) setGstRate(String(data.gst_rate));
        if (data.total && !amount) setAmount(String(data.total));
        setBuyerNameOnBill(data.buyer_name_on_bill || "");
        setBuyerGstinOnBill(data.buyer_gstin_on_bill || "");
        setBuyerMatchStatus(data.buyer_match_status || "");
        setBuyerMatchReason(data.buyer_match_reason || "");
        setDuplicateWarnings(Array.isArray(data.duplicate_warnings) ? data.duplicate_warnings : []);
        // Snapshot the raw AI-read numbers for the audit trail. The approver
        // may (and should) edit the visible inputs, but this untouched
        // snapshot goes to the backend so we always know what OCR saw.
        setAiExtracted({
            supplier_name: data.supplier_name || null,
            supplier_gstin: data.supplier_gstin || null,
            invoice_no: data.invoice_no || null,
            invoice_date: data.invoice_date || null,
            taxable_value: data.taxable_value ?? null,
            gst_amount: data.gst_amount ?? null,
            gst_rate: data.gst_rate ?? null,
            total: data.total ?? null,
            buyer_name_on_bill: data.buyer_name_on_bill || null,
            buyer_gstin_on_bill: data.buyer_gstin_on_bill || null,
            buyer_match_status: data.buyer_match_status || null,
            confidence: data.confidence ?? null,
        });
        // Auto-fill the expense head ONLY when this is a proper GST bill.
        // If GST detection failed, leave the head blank — the approver
        // must consciously pick it (per Sixth Element policy: no blind
        // classification of non-GST bills).
        const isGst = data.buyer_match_status === "match_gstin";
        if (isGst && data.suggested_head_id && !headId) {
            setHeadId(data.suggested_head_id);
            if (data.suggested_head_reason) {
                toast.success(`Head auto-set: ${data.suggested_head_name} · ${data.suggested_head_reason}`);
            }
        }
        const st = data.buyer_match_status;
        // NEVER auto-claim ITC — the approver must explicitly confirm the
        // bill matches GSTR-2B via the checkbox that appears below the
        // GST numbers. `hasGst` is now purely a signal that the OCR
        // detected a GST bill (used to gate what fields to show + require).
        setItcConfirmed(false);
        if (st === "match_gstin") {
            setHasGst(true);
            setLooseConfirmed(true);
            toast(`GST bill detected (confidence ${Math.round((data.confidence || 0.7) * 100)}%). Verify numbers, then confirm 2B match to claim ITC.`);
        } else if (st === "match_name_loose") {
            setHasGst(true); setLooseConfirmed(false); setShowLooseConfirm(true);
        } else if (st === "mismatch") {
            setHasGst(false); setLooseConfirmed(false);
            toast.error("⚠ This bill is addressed to someone else — cannot claim ITC.");
        } else if (st === "no_buyer_info") {
            setHasGst(false); setLooseConfirmed(false);
            toast(`Bill has GST but no buyer info printed. Decide manually.`);
        } else if (st === "no_gst") {
            setHasGst(false); setLooseConfirmed(false);
            toast.success(`Non-GST expense — pick vendor + head OR just head.`);
        } else if (st === "no_company_setup") {
            setHasGst(!!data.gst_amount); setLooseConfirmed(false);
            toast.warning("Set your company GSTIN in Settings so we can auto-verify.");
        } else {
            toast.success(`Extracted (confidence ${Math.round((data.confidence || 0.7) * 100)}%). Verify before posting.`);
        }
    };

    const runExtract = async (fileArg) => {
        const f = fileArg || file;
        if (!f) return toast.error("Attach the invoice file first");
        setExtracting(true);
        try {
            const fd = new FormData(); fd.append("file", f);
            const { data } = await api.post("/petty-cash/expenses/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
            _applyExtract(data, /*fromRescan=*/false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setExtracting(false); }
    };

    // Auto-trigger extract the moment a file is chosen — user asked for a
    // "drop file → loading → editable form" flow instead of a manual button.
    const handleFilePicked = (f) => {
        if (!f) return;
        setFile(f);
        // Kick off extract in the background — pass the file directly so we
        // don't race React state.
        runExtract(f);
    };

    const confirmLooseMatch = (yes) => {
        setShowLooseConfirm(false);
        if (yes) {
            setLooseConfirmed(true);
            setHasGst(true);
            toast.success("ITC will be claimed based on your confirmation.");
        } else {
            setLooseConfirmed(false);
            setBuyerMatchStatus("mismatch");
            setBuyerMatchReason("You confirmed this bill is not addressed to your company.");
            toast.error("Marked as not addressed to you — submission blocked.");
        }
    };

    const submit = async () => {
        const a = Number(amount);
        if (!headId) return toast.error("Pick an expense head");
        if (!a || a <= 0) return toast.error("Amount must be positive");
        // Block outright when the AI decided this bill isn't addressed to us — a
        // sane accountant would not accept it. The server also re-verifies.
        if (buyerMatchStatus === "mismatch") {
            return toast.error("This bill is not addressed to your company. Cannot submit as your expense.");
        }
        // Loose buyer-name match still requires explicit confirmation to
        // protect against wrong-company bills sneaking into ITC.
        if (hasGst && buyerMatchStatus === "match_name_loose" && !looseConfirmed) {
            return toast.error("Confirm the buyer-name match before posting.");
        }
        if (hasGst) {
            if (!supplierName.trim()) return toast.error("Supplier name required for GST invoice");
            if (!invoiceNo.trim()) return toast.error("Invoice number required for GST invoice");
            if (!Number(taxable) || Number(taxable) <= 0) return toast.error("Taxable value required for GST invoice");
        }
        setSaving(true);
        try {
            let docId = existingDocId;
            if (file) {
                const fd = new FormData(); fd.append("file", file);
                const { data } = await api.post("/petty-cash/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
                docId = data.document_id;
            }
            if (hasGst && !docId) return toast.error("Attach the invoice — proof is mandatory for GST claims");
            const body = {
                head_id: headId, amount: a, date, note, document_id: docId,
                ledger_override_id: ledgerOverrideId || null,
                has_gst: hasGst,
                supplier_name: hasGst ? supplierName : null,
                supplier_gstin: hasGst ? (supplierGstin || null) : null,
                invoice_no: hasGst ? invoiceNo : null,
                invoice_date: hasGst ? (invoiceDate || date) : null,
                taxable_value: hasGst ? Number(taxable) : null,
                gst_rate: hasGst ? Number(gstRate) : null,
                buyer_name_on_bill: buyerNameOnBill || null,
                buyer_gstin_on_bill: buyerGstinOnBill || null,
                buyer_match_status: buyerMatchStatus || null,
                vendor_ledger_id: pickedVendorId || null,
                skip_vendor_routing: skipVendorRouting,
                // ITC-confirm gate (Feb 2026)
                itc_confirmed: true,
                ai_extracted: aiExtracted,
            };
            // Approver flow: pending_approval submissions get classified + approved
            // via the /approve endpoint. Legacy edit path still hits PATCH.
            if (editing && editing.status === "pending_approval") {
                await api.post(`/petty-cash/expenses/${editing.id}/approve`, body);
                toast.success("Approved & posted");
            } else if (editing) {
                await api.patch(`/petty-cash/expenses/${editing.id}`, body);
                toast.success("Expense updated");
            } else {
                await api.post("/petty-cash/expenses", body);
                toast.success("Expense accounted");
            }
            onOpenChange(false);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    // Reject flow — mandatory reason, feedback loops back to submitter.
    const [rejecting, setRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const doReject = async () => {
        if (!editing) return;
        if ((rejectReason || "").trim().length < 4) {
            return toast.error("Please write a reason (min 4 chars) so the submitter can fix it.");
        }
        setSaving(true);
        try {
            await api.post(`/petty-cash/expenses/${editing.id}/reject`, { reason: rejectReason.trim() });
            toast.success("Rejected — submitter will be notified");
            setRejecting(false);
            setRejectReason("");
            onOpenChange(false);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    // Live journal preview
    const t = Number(taxable) || 0;
    const r = Number(gstRate) || 0;
    const gstAmt = +(t * r / 100).toFixed(2);
    const gross = +(t + gstAmt).toFixed(2);
    // Compare against ACTUAL configured home state (server-side is authoritative).
    const supplierState = (supplierGstin || "").trim().slice(0, 2);
    // Derived — is this a GST bill? Yes when AI detected a proper Bill-To
    // GSTIN match, or when the user explicitly confirmed a loose name match.
    // No manual checkbox — the AI is the source of truth.
    const isGstBill = buyerMatchStatus === "match_gstin"
        || (buyerMatchStatus === "match_name_loose" && looseConfirmed);
    // Keep the internal `hasGst` in sync with the derived value so the rest
    // of the code (submit payload builder, journal preview) stays untouched.
    useEffect(() => { setHasGst(isGstBill); }, [isGstBill]);
    const isIgst = isGstBill && supplierState && supplierState !== homeStateCode;
    const half = +(gstAmt / 2).toFixed(2);
    // AI has finished running and could not find a valid GST bill (either
    // no supplier GSTIN, no buyer info, or buyer mismatch). We show this
    // as a neutral "Failed to detect GST bill" banner in place of the ITC
    // banner and keep the expense head EMPTY so the approver must pick.
    const gstDetectionFailed = !extracting
        && !!buyerMatchStatus
        && !isGstBill
        && buyerMatchStatus !== "mismatch";  // mismatch has its own harder-red banner
    // GST Sec 17(5) — is the picked head on the block-list?
    const pickedHead = heads.find((h) => h.id === headId);
    const isBlockedHead = !!(pickedHead && pickedHead.blocked_credit);
    // Effective ITC eligibility — GST bill AND head not blocked.
    const claimsItc = isGstBill && !isBlockedHead;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pc-expense-dialog">
                <DialogHeader><DialogTitle>{editing ? "Edit expense" : "Account an expense"}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="text-[11px] text-stone-500">
                        You currently hold <b>₹ {inr(inHand)}</b>. Accounting an expense reduces this.
                    </div>

                    {/* 1. Proof + AI extract — always first, so the approver
                          sees the bill and the AI verdict BEFORE they classify. */}
                    <div className="border rounded-sm p-3 relative" style={{ borderColor: "var(--se-border)" }}>
                        <Label>Proof (image or PDF){isGstBill ? " *" : ""}</Label>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-sm text-xs cursor-pointer hover:bg-stone-50"
                                   style={{ borderColor: "var(--se-border)" }} data-testid="pc-expense-file-picker">
                                <Paperclip size={13} /> {file ? file.name : (existingDocId ? "Replace attachment" : "Attach bill · we'll auto-read it")}
                                <input type="file" accept="image/*,application/pdf" className="hidden"
                                       onChange={(e) => handleFilePicked(e.target.files?.[0] || null)} />
                            </label>
                            <CameraCaptureButton
                                onFile={(f) => handleFilePicked(f)}
                                label="Camera"
                                testid="pc-expense-camera-btn"
                            />
                            {existingDocId && !file
                                ? <a href={`${API}/documents/${existingDocId}/download`} target="_blank" rel="noopener noreferrer"
                                     className="text-[11px] underline text-blue-700">view current</a>
                                : null}
                            {existingDocId && !extracting ? (
                                <Button size="sm" variant="ghost"
                                        onClick={() => editing?.id ? runRescan(editing.id) : runExtract(file)}
                                        data-testid="pc-expense-extract"
                                        title="Re-run AI on the current file">
                                    Re-scan
                                </Button>
                            ) : null}
                        </div>
                        <div className="text-[11px] text-stone-500 mt-2">
                            The bill is auto-read by AI — supplier, GSTIN, invoice #, taxable, GST rate and best-fit expense head are pre-filled. Check everything before posting.
                        </div>
                        {extracting ? (
                            <div className="absolute inset-0 flex items-center justify-center rounded-sm z-10"
                                 style={{ background: "rgba(255,251,235,0.94)", border: "1px solid #f59e0b" }}
                                 data-testid="pc-expense-extract-loading">
                                <div className="text-center">
                                    <div className="inline-block w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-2" />
                                    <div className="text-sm font-medium text-amber-800">Reading your bill…</div>
                                    <div className="text-[11px] text-amber-700 mt-1">Extracting supplier, GST, expense head. This takes ~10-15s.</div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* 2. AI verdict — either the ITC-auto-claimed banner (Route A),
                          a red "Blocked credit under 17(5)" banner when the head
                          is on the block-list, a neutral "Failed to detect GST"
                          banner (Route B/C), or the hard mismatch banner. */}
                    {isGstBill && isBlockedHead ? (
                        <BlockedCreditBanner head={pickedHead} />
                    ) : isGstBill ? (
                        <SanityBanner status="match_gstin" reason={buyerMatchReason}
                                      buyerName={buyerNameOnBill} buyerGstin={buyerGstinOnBill} />
                    ) : buyerMatchStatus === "mismatch" ? (
                        <SanityBanner status="mismatch" reason={buyerMatchReason}
                                      buyerName={buyerNameOnBill} buyerGstin={buyerGstinOnBill} />
                    ) : gstDetectionFailed ? (
                        <FailedGstDetectBanner />
                    ) : null}

                    {/* Layer 2 duplicate-bill warning (soft, cross-module) */}
                    <DuplicateWarningBanner matches={duplicateWarnings} testid="pc-duplicate-warning" />

                    {/* 3. Expense head — placed AFTER AI so the approver sees
                          the verdict first. Auto-filled for Route A, empty
                          (and mandatory) for Route B/C. */}
                    <div>
                        <Label>Expense head *</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={headId}
                                onChange={(e) => setHeadId(e.target.value)} data-testid="pc-expense-head">
                            <option value="">— Select —</option>
                            {heads.map((h) => (
                                <option key={h.id} value={h.id}>{h.name} → {h.ledger_name}</option>
                            ))}
                        </select>
                    </div>

                    <details className="border rounded-sm px-3 py-2" style={{ borderColor: "var(--se-border)" }}>
                        <summary className="text-[11px] text-stone-500 cursor-pointer" data-testid="pc-override-toggle">
                            Book to a different ledger for THIS expense? (optional)
                        </summary>
                        <div className="mt-2">
                            <select className="w-full border rounded-sm px-3 py-2 text-sm" value={ledgerOverrideId}
                                    onChange={(e) => setLedgerOverrideId(e.target.value)} data-testid="pc-ledger-override">
                                <option value="">— Use head&apos;s default ledger —</option>
                                {(ledgers || [])
                                    .filter((l) => l.active !== false && !["bank", "cash", "customer", "vendor", "loan"].includes(l.party_type))
                                    .sort((a, b) => (a.group_name || "").localeCompare(b.group_name || "") || a.name.localeCompare(b.name))
                                    .map((l) => (
                                        <option key={l.id} value={l.id}>{l.name} · {l.group_name}</option>
                                    ))}
                            </select>
                            <div className="text-[10px] text-stone-500 mt-1">
                                Overrides where the Dr goes for this expense only. Useful when you buy a specific RM sub-category via petty cash and want the amount in the exact ledger rather than a generic head.
                            </div>
                        </div>
                    </details>

                    {/* 4. GST fields — only visible when AI confirmed a
                          proper GST bill (Route A). No manual checkbox. */}
                    {isGstBill ? (
                        <div className="grid grid-cols-2 gap-4 border rounded-sm p-4" style={{ borderColor: "var(--se-border)", background: "#fffbeb" }}>
                            <div className="col-span-2"><Label>Supplier name *</Label>
                                <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="ACE Hardware" data-testid="pc-gst-supplier" /></div>
                            <div><Label>Supplier GSTIN</Label>
                                <Input value={supplierGstin} onChange={(e) => setSupplierGstin(e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z9" data-testid="pc-gst-gstin" maxLength={15} />
                                {supplierGstin && supplierGstin.length === 15
                                    ? <div className="text-[10px] mt-1" style={{ color: isIgst ? "#0d6efd" : "#0f766e" }}>
                                        {isIgst ? `Inter-state (from ${supplierState} → ${homeStateCode}) — Input IGST` : `Intra-state (both ${supplierState}) — Input CGST + SGST`}
                                      </div>
                                    : null}
                            </div>
                            <div><Label>Invoice # *</Label>
                                <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} data-testid="pc-gst-inv-no" /></div>
                            <div><Label>Invoice date</Label>
                                <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="pc-gst-inv-date" /></div>
                            <div><Label>Taxable value (₹) *</Label>
                                <Input type="number" step="0.01" value={taxable} onChange={(e) => setTaxable(e.target.value)} data-testid="pc-gst-taxable" /></div>
                            <div><Label>GST rate</Label>
                                <select className="w-full border rounded-sm px-3 py-2 text-sm" value={gstRate}
                                        onChange={(e) => setGstRate(e.target.value)} data-testid="pc-gst-rate">
                                    {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r} %</option>)}
                                </select>
                            </div>
                            {t > 0 ? (
                                <div className="col-span-2 border-t pt-3 mt-1 text-xs" style={{ borderColor: "var(--se-border)" }}>
                                    {/* ITC-confirm gate REMOVED (Sep 2026) —
                                        always post Input CGST/SGST/IGST when
                                        the bill has GST. GSTR-2B mismatches
                                        are notified separately, not gated. */}
                                    <div className="font-medium mb-1">Journal preview{isBlockedHead ? " · ITC blocked (17(5))" : ""}</div>
                                    <div className="font-mono text-[11px] space-y-0.5">
                                        {isBlockedHead ? (
                                            <>
                                                <div>Dr <span className="text-stone-700">{heads.find((h) => h.id === headId)?.ledger_name || "—"}</span> · ₹ {inr(gross)} <span className="text-amber-700">(gross — Sec 17(5))</span></div>
                                                <div>Cr <span className="text-stone-700">Petty Cash — you</span> · ₹ {inr(gross)}</div>
                                            </>
                                        ) : (
                                            <>
                                                <div>Dr <span className="text-stone-700">{heads.find((h) => h.id === headId)?.ledger_name || "—"}</span> · ₹ {inr(t)}</div>
                                                {isIgst
                                                    ? <div>Dr <span className="text-stone-700">Input IGST</span> · ₹ {inr(gstAmt)}</div>
                                                    : gstAmt > 0 ? <>
                                                        <div>Dr <span className="text-stone-700">Input CGST</span> · ₹ {inr(gstAmt - half)}</div>
                                                        <div>Dr <span className="text-stone-700">Input SGST</span> · ₹ {inr(half)}</div>
                                                    </> : null}
                                                <div>Cr <span className="text-stone-700">Petty Cash — you</span> · ₹ {inr(gross)}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    <VendorRoutingPanel
                        isGstBill={isGstBill}
                        supplierName={supplierName}
                        supplierGstin={supplierGstin}
                        ledgers={ledgers}
                        pickedVendorId={pickedVendorId}
                        setPickedVendorId={setPickedVendorId}
                        skipVendorRouting={skipVendorRouting}
                        setSkipVendorRouting={setSkipVendorRouting}
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Amount paid (₹) *{isGstBill ? <span className="text-[10px] text-stone-500"> (auto)</span> : null}</Label>
                            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                                   readOnly={isGstBill} data-testid="pc-expense-amount" /></div>
                        <div><Label>Date *</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="pc-expense-date" /></div>
                    </div>
                    <div><Label>Note</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)}
                               placeholder="e.g. Petrol pump receipt #22" data-testid="pc-expense-note" /></div>
                </div>
                <DialogFooter>
                    {editing && editing.status === "pending_approval" && !rejecting ? (
                        <Button variant="outline" onClick={() => setRejecting(true)}
                                style={{ color: "#b91c1c", borderColor: "#b91c1c" }}
                                data-testid="pc-expense-reject-open">
                            Reject
                        </Button>
                    ) : null}
                    {rejecting ? (
                        <div className="flex-1 flex items-center gap-2">
                            <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                                   placeholder="Reason (visible to submitter)"
                                   className="flex-1 h-9"
                                   data-testid="pc-expense-reject-reason" />
                            <Button size="sm" variant="outline" onClick={() => { setRejecting(false); setRejectReason(""); }}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={doReject} disabled={saving}
                                    style={{ background: "#b91c1c", color: "#fff" }}
                                    data-testid="pc-expense-reject-confirm">
                                Confirm reject
                            </Button>
                        </div>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button onClick={submit}
                                    disabled={saving || buyerMatchStatus === "mismatch"}
                                    style={{ background: buyerMatchStatus === "mismatch" ? "#a8a29e" : "#f59e0b", color: "#fff" }}
                                    data-testid="pc-expense-submit">
                                {saving ? "Saving…"
                                  : buyerMatchStatus === "mismatch" ? "Submission blocked (bill isn't yours)"
                                  : (editing && editing.status === "pending_approval" ? "Approve & post"
                                     : editing ? "Save changes" : "Post expense")}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
            <LooseMatchConfirmDialog open={showLooseConfirm} onConfirm={confirmLooseMatch}
                                     buyerName={buyerNameOnBill} buyerGstin={buyerGstinOnBill} />
        </Dialog>
    );
}

// ============================================================================
// Small shared bits
// ============================================================================
function StatCard({ label, value, hint, tone = "dark", bold = false, isCount = false, testid }) {
    const bg = tone === "amber" ? "rgba(245,158,11,0.06)" : tone === "teal" ? "rgba(20,184,166,0.06)" : "#fff";
    const color = tone === "amber" ? "#b45309" : tone === "teal" ? "#0f766e" : "#0f172a";
    return (
        <div className="border rounded-md px-5 py-4" style={{ borderColor: "var(--se-border)", background: bg }} data-testid={testid}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`se-num text-2xl mt-1 ${bold ? "font-medium" : ""}`} style={{ color }}>
                {isCount ? value : `₹ ${inr(value || 0)}`}
            </div>
            {hint ? <div className="text-[11px] text-stone-500 mt-1">{hint}</div> : null}
        </div>
    );
}

function PettySection({ title, children, testid, accent }) {
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid={testid}>
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg" style={accent ? { color: accent } : undefined}>{title}</div>
            </div>
            {children}
        </div>
    );
}


// Buyer-sanity banner — surfaces right above the GST toggle after AI extract.
function SanityBanner({ status, reason, buyerName, buyerGstin }) {
    const styles = {
        match_gstin: { bg: "#f0fdf4", border: "#86efac", fg: "#166534", icon: "✓", label: "ITC auto-claimed — buyer GSTIN matches" },
        match_name_loose: { bg: "#fffbeb", border: "#fcd34d", fg: "#92400e", icon: "⚡", label: "Loose name match — awaiting your confirmation" },
        mismatch: { bg: "#fef2f2", border: "#fca5a5", fg: "#991b1b", icon: "⚠", label: "This bill isn't addressed to your company — submission blocked" },
        no_buyer_info: { bg: "#f8fafc", border: "#cbd5e1", fg: "#334155", icon: "ℹ", label: "Plain-paper receipt — decide GST claim manually" },
        no_company_setup: { bg: "#fef3c7", border: "#fbbf24", fg: "#78350f", icon: "!", label: "Configure your company details in Settings to enable auto-verification" },
    };
    const s = styles[status] || styles.no_company_setup;
    return (
        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="pc-sanity-banner"
             style={{ background: s.bg, borderColor: s.border, color: s.fg }}>
            <div className="font-medium flex items-center gap-2">
                <span aria-hidden="true">{s.icon}</span> {s.label}
            </div>
            {reason ? <div className="text-[11px] mt-1 opacity-90">{reason}</div> : null}
            {(buyerName || buyerGstin) ? (
                <div className="text-[11px] mt-1 opacity-80 font-mono">
                    Bill Bill-To: {buyerName || "(name missing)"} · GSTIN: {buyerGstin || "(GSTIN missing)"}
                </div>
            ) : null}
        </div>
    );
}


function LooseMatchConfirmDialog({ open, onConfirm, buyerName, buyerGstin }) {
    return (
        <Dialog open={open} onOpenChange={() => onConfirm(false)}>
            <DialogContent className="max-w-md" data-testid="pc-loose-confirm-dialog">
                <DialogHeader><DialogTitle>Is this bill addressed to your company?</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="text-stone-600">
                        The AI found a Bill-To section but it&apos;s not a GSTIN we could match. It could still be your bill (many small vendors don&apos;t write the GSTIN correctly).
                    </div>
                    <div className="border rounded-sm p-3 font-mono text-xs" style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                        <div><span className="text-stone-500">Buyer name on bill: </span><b>{buyerName || "—"}</b></div>
                        <div><span className="text-stone-500">GSTIN on bill: </span><b>{buyerGstin || "—"}</b></div>
                    </div>
                    <div className="text-[11px] text-stone-500">
                        Only confirm if you&apos;re certain the bill was issued to <b>you</b>. If it&apos;s a personal purchase or a bill for someone else, click &quot;No&quot; — the expense can still be booked without ITC.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onConfirm(false)} data-testid="pc-loose-no">No, not my bill</Button>
                    <Button onClick={() => onConfirm(true)} style={{ background: "#0d6efd", color: "#fff" }} data-testid="pc-loose-yes">
                        Yes, claim ITC
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
